import React, { useEffect, useRef, useState } from "react";

export type AdvancedMapSearchValues = {
  psn: string;
  vehicle: string;
  group: string;
  driver: string;
  poi: string;
};

type Scope = "vehicles" | "employees" | "poi";
type Criterion = { key: keyof AdvancedMapSearchValues; label: string; type: number };
type Suggestion = { value: string; id: string | number };

type Props = {
  show: boolean;
  onHide: () => void;
  values: AdvancedMapSearchValues;
  onApply: (values: AdvancedMapSearchValues) => void;
  onReset: () => void;
  userId: string;
};

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const toTags = (value: string) => value.split("||").map((item) => item.trim()).filter(Boolean);
const fromTags = (tags: Record<keyof AdvancedMapSearchValues, string[]>) => ({
  psn: tags.psn.join("||"),
  vehicle: tags.vehicle.join("||"),
  group: tags.group.join("||"),
  driver: tags.driver.join("||"),
  poi: tags.poi.join("||"),
});

const criteria: Record<Scope, Criterion[]> = {
  vehicles: [
    { key: "psn", label: "PSN", type: 12 },
    { key: "vehicle", label: "Véhicule", type: 11 },
    { key: "group", label: "Groupe du véhicule", type: 14 },
    { key: "vehicle", label: "Flotte iButton", type: 34 },
    { key: "vehicle", label: "Toute la flotte", type: 21 },
  ],
  employees: [
    { key: "driver", label: "Nom", type: 31 },
    { key: "driver", label: "Groupe d’employés", type: 14 },
    { key: "driver", label: "Code employé", type: 31 },
    { key: "driver", label: "Code iButton", type: 31 },
    { key: "driver", label: "Groupe iButton", type: 14 },
    { key: "driver", label: "Flotte iButton", type: 31 },
    { key: "driver", label: "Tous les employés", type: 31 },
  ],
  poi: [
    { key: "poi", label: "Nom", type: 36 },
    { key: "poi", label: "Type", type: 38 },
    { key: "poi", label: "POI classe", type: 38 },
    { key: "poi", label: "Tous les POI", type: 39 },
  ],
};

const AdvancedMapSearchDrawer: React.FC<Props> = ({ show, onHide, values, onApply, onReset, userId }) => {
  const [scope, setScope] = useState<Scope>("vehicles");
  const [criterion, setCriterion] = useState(criteria.vehicles[0]);
  const [tags, setTags] = useState<Record<keyof AdvancedMapSearchValues, string[]>>(() => ({
    psn: toTags(values.psn), vehicle: toTags(values.vehicle), group: toTags(values.group), driver: toTags(values.driver), poi: toTags(values.poi),
  }));
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const requestId = useRef(0);

  useEffect(() => setTags({
    psn: toTags(values.psn), vehicle: toTags(values.vehicle), group: toTags(values.group), driver: toTags(values.driver), poi: toTags(values.poi),
  }), [values]);

  const changeScope = (nextScope: Scope) => {
    setScope(nextScope);
    setCriterion(criteria[nextScope][0]);
    setQuery("");
    setSuggestions([]);
  };

  const search = async (value: string) => {
    setQuery(value);
    if (!value.trim() || !userId) {
      setSuggestions([]);
      return;
    }

    const currentRequest = ++requestId.current;
    try {
      const response = await fetch(`${backendUrl}/api/search/${criterion.type}/${encodeURIComponent(value.trim())}/${encodeURIComponent(userId)}`, { mode: "cors" });
      if (!response.ok) throw new Error(`Autocomplete failed: ${response.status}`);
      const payload = await response.json();
      if (currentRequest !== requestId.current) return;
      const result = Array.isArray(payload?.data) ? payload.data : [];
      const mapped = result.map((item: any): Suggestion => {
        if (criterion.type === 12) return { value: item.psn_dispositif, id: item.id_dispositif };
        if (criterion.type === 14) return { value: item.nom_groupe, id: item.id_groupe };
        if (criterion.type === 31) return { value: `${item.nom_conducteur || ""} ${item.prenom_conducteur || ""}`.trim(), id: item.id_dispositif };
        if (criterion.type >= 36) return { value: item.namegeof, id: item.idgeof };
        return { value: item.immatriculation_vehicule, id: item.id_dispositif };
      }).filter((item: Suggestion) => item.value);
      setSuggestions(mapped.slice(0, 10));
    } catch {
      if (currentRequest === requestId.current) setSuggestions([]);
    }
  };

  const selectSuggestion = (value: string) => {
    setTags((current) => current[criterion.key].includes(value) ? current : ({ ...current, [criterion.key]: [...current[criterion.key], value] }));
    setQuery("");
    setSuggestions([]);
  };

  const addQueryAsTag = () => {
    const value = query.trim();
    if (!value) return;
    selectSuggestion(value);
  };

  const removeTag = (key: keyof AdvancedMapSearchValues, value: string) => {
    setTags((current) => ({ ...current, [key]: current[key].filter((tag) => tag !== value) }));
  };

  const apply = () => {
    onApply(fromTags(tags));
    onHide();
  };

  const reset = () => {
    setTags({ psn: [], vehicle: [], group: [], driver: [], poi: [] });
    setQuery("");
    setSuggestions([]);
    onReset();
  };

  return (
    <>
      <button
        type="button"
        className={`map-search-advanced-backdrop ${show ? "is-open" : ""}`}
        onClick={onHide}
        aria-label="Fermer la recherche avancée"
        tabIndex={show ? 0 : -1}
      />
      <section className={`map-search-advanced-panel ${show ? "is-open" : ""}`} aria-label="Recherche avancée" aria-hidden={!show}>
      <div className="map-search-advanced-toolbar">
        <button type="button" className="map-search-advanced-icon" onClick={onHide} aria-label="Fermer"><i className="las la-search" /></button>
        <button type="button" className={scope === "vehicles" ? "active" : ""} onClick={() => changeScope("vehicles")}>Véhicules <span>×</span></button>
        <button type="button" className={scope === "employees" ? "active" : ""} onClick={() => changeScope("employees")}>Employés <span>×</span></button>
        <button type="button" className={scope === "poi" ? "active" : ""} onClick={() => changeScope("poi")}>POI <span>×</span></button>
        <button type="button" className="map-search-advanced-close" onClick={onHide} aria-label="Fermer">×</button>
      </div>

      <div className="map-search-advanced-criteria">
        {criteria[scope].map((item, index) => (
          <button key={`${item.label}-${index}`} type="button" className={criterion === item ? "active" : ""} onClick={() => { setCriterion(item); setQuery(""); setSuggestions([]); }}>{item.label}</button>
        ))}
      </div>

      <div className="map-search-advanced-input-wrap">
        <input value={query} onChange={(event) => search(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") addQueryAsTag(); }} autoFocus aria-label={criterion.label} placeholder={`Rechercher ${criterion.label.toLowerCase()}`} />
        <button type="button" onClick={addQueryAsTag} aria-label="Ajouter">+</button>
        <button type="button" onClick={apply} aria-label="Rechercher"><i className="las la-search" /></button>
        {!!suggestions.length && <div className="map-search-drawer__suggestions" role="listbox">{suggestions.map((suggestion) => <button key={`${suggestion.id}-${suggestion.value}`} type="button" onClick={() => selectSuggestion(suggestion.value)}>{suggestion.value}</button>)}</div>}
      </div>

      <div className="map-search-advanced-tags" aria-label="Critères sélectionnés">
        {(Object.keys(tags) as Array<keyof AdvancedMapSearchValues>).flatMap((key) => tags[key].map((tag) => (
          <button key={`${key}-${tag}`} type="button" className="map-search-advanced-tag" onClick={() => removeTag(key, tag)}>{tag} ×</button>
        )))}
      </div>

      <div className="map-search-advanced-actions">
        <button type="button" onClick={reset}>Réinitialiser</button>
        <button type="button" className="primary" onClick={apply}>Rechercher</button>
      </div>
      </section>
    </>
  );
};

export default AdvancedMapSearchDrawer;
